export type AdbApplicationState = "enabled" | "disabled" | "uninstalled";
export type AdbApplicationScope = "system" | "user" | "other";

export interface AdbApplication {
  packageName: string;
  apkPath?: string;
  uid?: number;
  state: AdbApplicationState;
  scope: AdbApplicationScope;
}

export function parsePackageList(allOutput: string, systemOutput: string, userOutput: string, disabledOutput: string): AdbApplication[] {
  const systemPackages = parsePackageNames(systemOutput);
  const userPackages = parsePackageNames(userOutput);
  const disabledPackages = parsePackageNames(disabledOutput);
  const applications = new Map<string, AdbApplication>();

  for (const line of allOutput.split(/\r?\n/)) {
    const parsed = parsePackageLine(line);
    if (!parsed) continue;
    applications.set(parsed.packageName, {
      ...parsed,
      state: parsed.apkPath ? disabledPackages.has(parsed.packageName) ? "disabled" : "enabled" : "uninstalled",
      scope: systemPackages.has(parsed.packageName) ? "system" : userPackages.has(parsed.packageName) ? "user" : "other",
    });
  }

  return [...applications.values()].sort((left, right) => left.packageName.localeCompare(right.packageName));
}

export function parsePackageNames(output: string): Set<string> {
  const result = new Set<string>();
  for (const line of output.split(/\r?\n/)) {
    const parsed = parsePackageLine(line);
    if (parsed) result.add(parsed.packageName);
  }
  return result;
}

function parsePackageLine(line: string): Pick<AdbApplication, "packageName" | "apkPath" | "uid"> | undefined {
  const value = line.trim().replace(/^package:/, "");
  if (!value) return undefined;
  const uidMatch = value.match(/\s+uid:(\d+)$/);
  const uid = uidMatch ? Number(uidMatch[1]) : undefined;
  const withoutUid = uidMatch ? value.slice(0, uidMatch.index).trim() : value;
  const separator = withoutUid.lastIndexOf("=");
  if (separator < 0) return isPackageName(withoutUid) ? { packageName: withoutUid, uid } : undefined;
  const apkPath = withoutUid.slice(0, separator) || undefined;
  const packageName = withoutUid.slice(separator + 1);
  if (!isPackageName(packageName)) return undefined;
  return { packageName, apkPath, uid };
}

function isPackageName(value: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(value);
}
